// Code 128 puro — do SKU ao desenho das barras. Sem dependência, sem DOM.
//
// POR QUE UM CÓDIGO PRÓPRIO, E POR QUE ELE É O SKU
// Peça de fornecedor pequeno chega sem EAN. Sem código na caixa, achar a peça
// depende de alguém digitar o nome certo — e é aí que o balcão perde tempo.
// Gerando o nosso, toda peça passa a ter um código que o leitor bipa.
//
// O conteúdo é o SKU literal (`FRE-0001`), e isso não é acaso: `buscar_pecas`
// já dá relevância 1.0 para match de SKU normalizado, e `codigoBateExato` (em
// bip.ts) já inclui `sku` na lista. Ou seja, bipar a nossa etiqueta cai no
// MESMO caminho do bip de um EAN de fábrica — sem coluna nova, sem tabela
// nova, sem caminho novo para manter. O hífen some na normalização dos dois
// lados (norm_code), então tanto faz o leitor devolver com ou sem ele.
//
// POR QUE SUBSET B PURO, SEM OTIMIZAR PARA C
// O subset C codifica pares de dígitos e economizaria UM símbolo num SKU como
// `FRE-0001`. Em troca, exigiria trocar de subset no meio do código — e a
// troca é a parte do Code 128 onde os erros moram. Um símbolo a mais não muda
// nada no papel; um bug de troca de subset gera etiqueta que o leitor recusa.

/**
 * Os 107 padrões do Code 128, como larguras de módulo (barra, espaço, barra,
 * espaço, barra, espaço). Cada símbolo soma 11 módulos — exceto o stop, que
 * tem 13 e uma barra a mais no fim.
 *
 * O índice é o VALOR do símbolo, que é o que entra no cálculo do checksum.
 * Em subset B, o valor de um caractere imprimível é `charCode - 32`.
 */
const PADROES = [
  '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312',
  '132212', '221213', '221312', '231212', '112232', '122132', '122231', '113222',
  '123122', '123221', '223211', '221132', '221231', '213212', '223112', '312131',
  '311222', '321122', '321221', '312212', '322112', '322211', '212123', '212321',
  '232121', '111323', '131123', '131321', '112313', '132113', '132311', '211313',
  '231113', '231311', '112133', '112331', '132131', '113123', '113321', '133121',
  '313121', '211331', '231131', '213113', '213311', '213131', '311123', '311321',
  '331121', '312113', '312311', '332111', '314111', '221411', '431111', '111224',
  '111422', '121124', '121421', '141122', '141221', '112214', '112412', '122114',
  '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111',
  '111242', '121142', '121241', '114212', '124112', '124211', '411212', '421112',
  '421211', '212141', '214121', '412121', '111143', '111341', '131141', '114113',
  '114311', '411113', '411311', '113141', '114131', '311141', '411131', '211412',
  '211214', '211232', '2331112',
];

/** Valores especiais. Só o que este módulo usa — nada de FNC nem de troca. */
const START_B = 104;
const STOP = 106;

/** Ponto de partida do checksum e limites do subset B em ASCII. */
const ASCII_MIN = 32;   // espaço
const ASCII_MAX = 126;  // ~

/**
 * Os valores dos símbolos: [START_B, ...dados, checksum, STOP].
 *
 * O checksum é `(START_B + Σ valorᵢ × posiçãoᵢ) mod 103`, com posição
 * começando em 1 no primeiro dado — é a soma ponderada da própria spec.
 * Errar isso gera um código que o leitor simplesmente ignora, sem dizer por
 * quê; por isso existe teste de round-trip.
 */
export function simbolosCode128(texto: string): number[] {
  if (!texto) throw new Error('Código de barras vazio.');

  const dados: number[] = [];
  for (const ch of texto) {
    const c = ch.codePointAt(0)!;
    if (c < ASCII_MIN || c > ASCII_MAX) {
      throw new Error(`Caractere fora do Code 128-B: "${ch}". Use apenas ASCII imprimível.`);
    }
    dados.push(c - ASCII_MIN);
  }

  const soma = dados.reduce((acc, v, i) => acc + v * (i + 1), START_B);
  return [START_B, ...dados, soma % 103, STOP];
}

/**
 * Larguras alternando barra/espaço, começando em BARRA. É o desenho cru: cada
 * número é quantos módulos aquela faixa ocupa.
 */
export function modulosCode128(texto: string): number[] {
  return simbolosCode128(texto)
    .flatMap((v) => PADROES[v]!.split('').map(Number));
}

export interface BarraCode128 {
  /** Posição da barra, em módulos, a partir do início do código. */
  x: number;
  largura: number;
}

/**
 * Só as BARRAS (os espaços viram lacuna), prontas para virar `<rect>`.
 *
 * Devolve também a largura total em módulos, que é o que o SVG usa no viewBox:
 * assim o desenho escala sozinho e nenhuma barra fica com meio pixel.
 */
export function barrasCode128(texto: string): { barras: BarraCode128[]; larguraModulos: number } {
  const larguras = modulosCode128(texto);
  const barras: BarraCode128[] = [];
  let x = 0;

  larguras.forEach((largura, i) => {
    // Índice par é barra, ímpar é espaço — a sequência sempre começa em barra.
    if (i % 2 === 0) barras.push({ x, largura });
    x += largura;
  });

  return { barras, larguraModulos: x };
}

/**
 * A margem clara que o leitor precisa dos dois lados. Dez módulos é o mínimo
 * da spec; sem ela, o leitor perde o começo do código e a etiqueta "não bipa"
 * sem motivo aparente.
 */
export const QUIET_ZONE_MODULOS = 10;
